import React, { FC, useState, useCallback, useEffect } from 'react';
import { Box, Container, useTheme, useMediaQuery, Card } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';
import Notification from '../../components/Notification';
import CustomTheme from '../AuthenticatedLayout/CustomTheme';
import CustomerTheme from '../AuthenticatedLayout/CustomerTheme';
import WebSocket from '../../screens/Authenticated/WebSocket/WebSocket';
import axiosInstance from '../../utils/axios';
import { URLS } from '../../utils/constants/urls';
import { identifiers } from '../../utils/constants/identifiers';
import { fetchFromStorage } from '../../utils/storage';
import {
  getOrganizationList,
  organizationInfo,
  setSelectedOrganization,
  setTheme,
  setUser,
} from '../../utils/redux/reducer/authentication-slice';
import { publishNotification } from '../../utils/validators/HelperFunctions';
import { AlertColor } from '@mui/material';
const isWebsocket = false;
const AuthLayout: FC<{ Component: FC }> = ({ Component }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSalesReport = location.pathname === '/sales-report';
  const isDashboard = location.pathname === '/dashboard';
  const isSpecialPage = isSalesReport || isDashboard;

  const org = useSelector(organizationInfo) as any;

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [isCustomThemeDialogOpen, setIsCustomThemeDialogOpen] = useState<boolean>(false);
  const [isCustomerThemeDialogOpen, setIsCustomerThemeDialogOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const initOrg = fetchFromStorage(identifiers.organization) as any;
  const tenantId = fetchFromStorage('tenantId');

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      const { status, data } = await axiosInstance.get(URLS.userProfile);
      if (status === 200) {
        dispatch(setUser(data));
      }
    } catch (error: any) {
      handleCatch(error);
    }
  }, [dispatch]);

  const fetchThemeConfig = useCallback(async () => {
    try {
      const { status, data } = await axiosInstance(URLS.getTheme);
      if (status === 200) {
        dispatch(setTheme({ ...data, logoBackgroundColor: data.logoBackgroundColor || '#A4E3F3' }));
      }
    } catch (e) {
      console.log(e);
    }
  }, [dispatch]);

  const onWebSocketNotification = useCallback((message: any, destination: any) => {
    if (destination === '/topic/bookings' || destination === '/user/queue/bookings') {
      publishNotification(
        message.title,
        message.description,
        message.logoUrl !== null ? message.logoUrl : identifiers.logo,
      );
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    if (initOrg !== '*') {
      dispatch(getOrganizationList() as any);
    }
    fetchThemeConfig();
  }, [fetchThemeConfig, dispatch, initOrg]);

  useEffect(() => {
    if (initOrg !== '*') {
      dispatch(setSelectedOrganization(org?.selectedOrganization));
    }
  }, [org, initOrg, dispatch]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#eef2f6' }}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}

      {isWebsocket && <WebSocket handleNotification={onWebSocketNotification} tenantId={tenantId} />}

      <CustomTheme open={isCustomThemeDialogOpen} setOpen={setIsCustomThemeDialogOpen} />
      <CustomerTheme open={isCustomerThemeDialogOpen} setOpen={setIsCustomerThemeDialogOpen} />

      {/* Sidebar */}
      <Sidebar isMobile={isMobile} open={sidebarOpen} onToggle={handleSidebarToggle} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: {
            xs: '100%',
            md: `calc(100% - ${sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH}px)`,
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}>
        <Header onMenuClick={handleSidebarToggle} sidebarOpen={sidebarOpen} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            ...(isSpecialPage
              ? {
                  pt: 1,
                  px: 0,
                  mt: { xs: '50px', sm: '64px' },
                  backgroundColor: 'transparent',
                }
              : {
                  py: 1,
                  px: 0.5,
                  mt: { xs: '50px', sm: '64px' },
                  backgroundColor: '#eef2f6',
                }),
          }}>
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              height: '100%',
              '&.MuiContainer-root': { px: 0 },
            }}>
            {isSpecialPage ? (
              <Component />
            ) : (
              <Card
                sx={{
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1.5, sm: 1.5 },
                  height: '100%',
                  width: '100%',
                  borderRadius: 1,
                }}>
                <Component />
              </Card>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
